import { useMemo, useState } from "react"
import {
  Wallet,
  Store,
  Paperclip,
  GitBranch,
  List,
  Check,
  AlertTriangle,
  FileText,
  FileSignature,
  FileCheck2,
  Receipt,
  Truck,
  Tag,
  ShoppingCart,
} from "lucide-react"
import { Card, Button, StatusBadge, Currency, ProgressBar, budgetHealth, clampPct } from "../../ui"
import VendorComparisonModal from "../proposals/VendorComparisonModal"
import NoteCell from "./NoteCell"
import {
  NO_QUOTE_LABEL,
  quotedItems,
  hasQuote,
  proposalTotal,
  formatVND,
  formatVNDSigned,
  formatDate,
  approvalModeLabel,
  formatQuorum,
  formatMaxAmount,
  linePeriodLabel,
  LINE_PERIOD_COLOR,
} from "../../constants"
import { HScroll } from "procurement/design/components/HScroll"

// Order-detail cards shared by the manager Order Review screen and the
// requester Order Request detail screen — both consume the same
// OrderRequestController response shape (budgetContext / vendorProposals /
// documents / approvalProgress), just with a different action sidebar.

const DOC_ICONS = {
  contract: FileSignature,
  invoice: Receipt,
  delivery_note: Truck,
  acceptance_certificate: FileCheck2,
  payment_request: FileText,
  budget_approval: Wallet,
  price_approval: Tag,
}

export const ACTION_VERB = {
  approve: "approved",
  reject: "rejected",
  info_request: "requested more info",
  resubmit: "resubmitted",
  submit: "submitted",
  reroute: "rerouted",
}

export const BudgetContextCard = ({ ctx, orderTotal }) => {
  if (!ctx) {
    return (
      <Card icon={<Wallet aria-hidden />} title="Budget Context">
        <div className="pc-alert pc-alert--danger" role="alert" style={{ marginBottom: 0 }}>
          <span className="pc-alert-ico">
            <AlertTriangle aria-hidden />
          </span>
          <div className="pc-alert-body">
            <div className="pc-alert-title">No active budget found</div>
            <div className="pc-alert-text">
              This order cannot move through approval until a budget exists for its selected category
              in the current fiscal year.
            </div>
          </div>
        </div>
      </Card>
    )
  }
  const total = Number(ctx.total) || 0
  const remaining = Number(ctx.remaining) || 0
  const after =
    ctx.remainingAfterThisOrder != null
      ? Number(ctx.remainingAfterThisOrder)
      : remaining - (Number(orderTotal) || 0)
  const health = budgetHealth(remaining, total)
  const usedPct = total > 0 ? clampPct(((total - remaining) / total) * 100) : 0
  // BE flags the budget itself as over-spent — not inferred from this order.
  const over = !!ctx.isOverBudget
  const overSpent = Number(ctx.overSpentAmount) || 0

  // "Total budget" (ctx.total) is only this one month's own new grant, not
  // useful on its own once accumulateUnspent is in play — show the
  // authoritative "Remaining budget" (cumulative) instead, and pair it with
  // "After purchase" (remainingAfterThisOrder) rather than re-showing the
  // same "Remaining" figure twice.
  const lines = [
    ["Remaining budget", ctx.remaining],
    ["Committed", ctx.committed],
    ["Approved – unpaid", ctx.approvedUnpaid],
    ["Approved – paid", ctx.approvedPaid],
    ["After purchase", ctx.remainingAfterThisOrder],
  ]

  return (
    <Card icon={<Wallet aria-hidden />} title="Budget Context" subtitle="What this decision spends against.">
      <div className="pc-budget-lines">
        {lines.map(([label, value]) => (
          <div key={label}>
            <div className="pc-budget-line-label">{label}</div>
            <div className="pc-budget-line-value">
              <Currency value={value} align="left" />
            </div>
            {label === "Remaining budget" && ctx.accumulatedThroughMonth != null && (
              <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>
                Accumulated through month {ctx.accumulatedThroughMonth}
              </div>
            )}
          </div>
        ))}
      </div>

      <ProgressBar pct={usedPct} color={health.color} />

      <div className={`pc-remaining-after${over ? " pc-remaining-after--over" : ""}`}>
        <span className="pc-remaining-after-label">
          Remaining after approving this order
        </span>
        <span className="pc-remaining-after-value">
          {after < 0 ? "-" : ""}
          {formatVND(Math.abs(after))}
        </span>
      </div>

      {over && (
        <div className="pc-alert pc-alert--danger" role="alert" style={{ marginTop: 14, marginBottom: 0 }}>
          <span className="pc-alert-ico">
            <AlertTriangle aria-hidden />
          </span>
          <div className="pc-alert-body">
            <div className="pc-alert-title">Over budget</div>
            <div className="pc-alert-text">
              This budget is over-spent by {formatVND(overSpent)}.
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

// `lines` (optional) is the order's own lines — a proposal's items only carry
// itemId/proposedAmount/note (no quantity), so quantity is looked up from the
// matching order line by itemId to show alongside each quoted item.
// `orderId` (optional): when given, shows a "Review and compare quotation"
// button that opens VendorComparisonModal (checkbox pick + reason, same
// mechanism as the create-order form's panel) — lets whoever's viewing this
// order re-pick the winning vendor without going back to the create form.
export const VendorProposals = ({ proposals, lines, orderId }) => {
  const rows = proposals || []
  const cheapest = useMemo(() => {
    const totals = rows.map(proposalTotal).filter((n) => n > 0)
    return totals.length ? Math.min(...totals) : null
  }, [rows])
  const quantityByItemId = {}
  ;(lines || []).forEach((l) => {
    if (l.itemId != null) quantityByItemId[l.itemId] = l.quantity
  })
  const [compareOpen, setCompareOpen] = useState(false)
  const hasQuotes = rows.some(hasQuote)

  return (
    <Card
      icon={<Store aria-hidden />}
      title="Vendor Proposals"
      action={
        orderId && (
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasQuotes}
            onClick={() => setCompareOpen(true)}
            title={hasQuotes ? undefined : "Waiting for at least one vendor to submit a quotation."}
          >
            Review and compare quotation
          </Button>
        )
      }
    >
      {orderId && (
        <VendorComparisonModal
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          proposals={rows}
          sourceType="order"
          sourceId={orderId}
        />
      )}
      {rows.length === 0 ? (
        <p className="pc-muted">No vendor proposals attached.</p>
      ) : (
        <HScroll className="pc-table-wrap">
          <table className="pc-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Items quoted</th>
                <th className="pc-th-right">Total</th>
                <th>Deadline</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const total = proposalTotal(p)
                const diff = cheapest != null && total > 0 ? total - cheapest : null
                return (
                  <tr key={p.id ?? i} className={p.isSelected ? "pc-row-selected" : undefined}>
                    <td className="pc-td-strong">
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {p.supplierName || p.supplier?.name || "—"}
                        {p.isSelected && <StatusBadge label="Selected" status="approved" />}
                      </div>
                      {p.supplier?.email && <div className="pc-muted">{p.supplier.email}</div>}
                    </td>
                    <td>
                      {!hasQuote(p) ? (
                        <span className="pc-muted">{NO_QUOTE_LABEL}</span>
                      ) : (
                        quotedItems(p).map((it) => (
                          <div key={it.id ?? it.itemId}>
                            {it.itemName}
                            {(it.offCatalog ? it.quantity : quantityByItemId[it.itemId]) != null
                              ? ` × ${it.offCatalog ? it.quantity : quantityByItemId[it.itemId]}`
                              : ""}
                            {it.note ? ` — ${it.note}` : ""}
                          </div>
                        ))
                      )}
                    </td>
                    <td className="pc-td-right">
                      {hasQuote(p) && <Currency value={total} />}
                      {p.deliveryFee != null && (
                        <div className="pc-muted">+ phí giao {formatVND(p.deliveryFee)} (không cộng vào tổng)</div>
                      )}
                      {rows.length >= 2 && diff != null && (
                        <div className={`pc-diff${diff === 0 ? " pc-diff--zero" : ""}`}>
                          {diff === 0 ? "Lowest" : formatVNDSigned(diff)}
                        </div>
                      )}
                    </td>
                    <td>{p.expiredAt ? formatDate(p.expiredAt) : "—"}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </HScroll>
      )}
    </Card>
  )
}

// Order lines (item × center × qty) — the same data the create/edit form
// shows, but read-only here. `centerNameById` is optional; falls back to a
// bare "#<id>" label when the caller hasn't fetched center names.
export const OrderLines = ({ lines, centerNameById }) => {
  const rows = lines || []
  if (rows.length === 0) return null
  const showMonth = rows.some((l) => linePeriodLabel(l))

  return (
    <Card icon={<List aria-hidden />} title="Order Lines">
      <HScroll className="pc-table-wrap">
        <table className="pc-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Center</th>
              {showMonth && <th>Month</th>}
              <th>Type</th>
              <th className="pc-th-right">Qty</th>
              <th className="pc-th-right">Unit price</th>
              <th className="pc-th-right">Amount</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l, i) => (
              <tr key={l.id ?? i}>
                <td className="pc-td-strong">
                  <NoteCell text={l.itemName || l.offCatalogItemName} hover minWidth={240} maxWidth={360} />
                  {!l.itemId && l.offCatalogItemName && (
                    <div className="pc-muted" style={{ fontSize: 12, fontWeight: 400 }}>
                      Off-catalog{l.offCatalogUnit ? ` · ${l.offCatalogUnit}` : ""}
                    </div>
                  )}
                  {l.requestType === "replacement" && l.replacedAssetCode && (
                    <div className="pc-muted" style={{ fontSize: 12, fontWeight: 400 }}>
                      Replaces {l.replacedAssetCode}
                    </div>
                  )}
                </td>
                <td>{centerNameById?.[l.centerId] || `#${l.centerId}`}</td>
                {showMonth && (
                  <td style={{ whiteSpace: "nowrap", color: LINE_PERIOD_COLOR[l.period?.budgetStatus] }}>
                    {linePeriodLabel(l) || "—"}
                  </td>
                )}
                <td style={{ textTransform: "capitalize" }}>{l.requestType || "new"}</td>
                <td className="pc-td-right">{l.quantity}</td>
                <td className="pc-td-right">
                  <Currency value={l.unitPrice} />
                </td>
                <td className="pc-td-right">
                  <Currency value={l.amount} />
                </td>
                <td>
                  <NoteCell text={l.note} />
                  {l.ticketLink && (
                    <div>
                      <a href={l.ticketLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                        Ticket ↗
                      </a>
                    </div>
                  )}
                  {l.proofImageUrl && (
                    <div>
                      <a href={l.proofImageUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                        Proof photo ↗
                      </a>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HScroll>
    </Card>
  )
}

// Purchase Orders already linked to this order — `purchases[]` is always an
// array (never null/undefined). An order can end up with more than one PO
// (e.g. split across vendors), so this is informational, not a 1:1 pointer.
export const LinkedPurchases = ({ purchases }) => {
  const rows = purchases || []
  if (rows.length === 0) return null

  return (
    <Card icon={<ShoppingCart aria-hidden />} title="Linked Purchase Orders">
      <HScroll className="pc-table-wrap">
        <table className="pc-table">
          <thead>
            <tr>
              <th>PO Code</th>
              <th>Vendor</th>
              <th className="pc-th-right">Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="pc-td-strong">{p.code}</td>
                <td>{p.vendorName || "—"}</td>
                <td className="pc-td-right">
                  <Currency value={p.totalAmount} />
                </td>
                <td>
                  <StatusBadge status={p.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HScroll>
    </Card>
  )
}

export const Attachments = ({ documents }) => {
  const docs = documents || []
  return (
    <Card icon={<Paperclip aria-hidden />} title="Attachments">
      {docs.length === 0 ? (
        <p className="pc-muted">No documents attached.</p>
      ) : (
        <div className="pc-attach">
          {docs.map((d, i) => {
            const Ico = DOC_ICONS[d.docType] || FileText
            return (
              <a
                key={d.id ?? i}
                className="pc-attach-row"
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="pc-attach-ico">
                  <Ico aria-hidden />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="pc-attach-name">{d.name || "Document"}</div>
                  <div className="pc-attach-type" style={{ textTransform: "capitalize" }}>
                    {(d.docType || "file").replace(/_/g, " ")}
                  </div>
                </div>
              </a>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// Real API gives just `quorum` (a count), not a required/of pair — render it
// with the same "needs N approvers" phrasing used elsewhere.
const quorumText = (step) => {
  const parts = []
  if (step.mode) parts.push(approvalModeLabel(step.mode))
  if (step.quorum) parts.push(formatQuorum(step.quorum))
  return parts.join(" · ")
}

export const ApprovalProgress = ({ steps, title = "Approval Progress", subtitle, emptyText = "No approval route resolved yet." }) => {
  const list = steps || []
  return (
    <Card icon={<GitBranch aria-hidden />} title={title} subtitle={subtitle}>
      {list.length === 0 ? (
        <p className="pc-muted">{emptyText}</p>
      ) : (
        <div className="pc-ap">
          {list.map((step, idx) => {
            const isLast = idx === list.length - 1
            const nodeMod =
              step.state === "done"
                ? "pc-ap-node--done"
                : step.state === "current"
                ? "pc-ap-node--current"
                : ""
            const approverList = step.approverNames || step.approvers
            const approvers = Array.isArray(approverList)
              ? approverList.join(", ")
              : approverList || "—"
            const stepNo = step.stepOrder ?? step.step ?? idx + 1
            return (
              <div className={`pc-ap-item pc-ap-item--${step.state}`} key={stepNo}>
                <div className="pc-ap-rail">
                  <span className={`pc-ap-node ${nodeMod}`}>
                    {step.state === "done" ? <Check aria-hidden /> : stepNo}
                  </span>
                  {!isLast && (
                    <span
                      className={`pc-ap-connector${step.state === "done" ? " pc-ap-connector--done" : ""}`}
                    />
                  )}
                </div>
                <div className="pc-ap-body">
                  <div className="pc-ap-head">
                    <span className="pc-ap-name">{approvers}</span>
                    {quorumText(step) && <span className="pc-ap-quorum">{quorumText(step)}</span>}
                    {formatMaxAmount(step.maxAmount) && (
                      <span className="pc-ap-quorum">{formatMaxAmount(step.maxAmount)}</span>
                    )}
                  </div>
                  {step.actions && step.actions.length > 0 && (
                    <div className="pc-ap-actions">
                      {step.actions.map((a, ai) => (
                        <div className="pc-ap-action" key={ai}>
                          <div className="pc-ap-action-l">
                            <strong>{a.actor || "Someone"}</strong>{" "}
                            {ACTION_VERB[a.action || a.type] || a.action || a.type}
                            {(a.actedAt || a.at) ? ` · ${formatDate(a.actedAt || a.at)}` : ""}
                          </div>
                          {a.note && <div className="pc-ap-action-note">“{a.note}”</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}
